import { useState, useEffect } from "react";
import { PageHeader } from "@/components/shared/PageHeader";
import { Reveal, SectionHeader } from "@/components/shared/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, Tent, Info } from "lucide-react";
import { toast } from "sonner";
import { DepotAreas } from "@/components/home/DepotAreas";
import { DepotLookup } from "@/components/depot/DepotLookup";

const DEPOT_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_novemberkasan-live/artifacts/t1i2pj9y_ChatGPT%20Image%20Sep%204%2C%202026%2C%2011_43_53%20AM.png";

export default function DepaBokning() {
  const [spots, setSpots] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(true);
  const [form, setForm] = useState({ spot_number: "", name: "", email: "", phone: "", svemo_ta: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [area, setArea] = useState("");

  const loadSpots = () => {
    setLoadingSpots(true);
    api.depotSpots().then(setSpots).catch(() => setSpots([])).finally(() => setLoadingSpots(false));
  };
  useEffect(loadSpots, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.spot_number) { toast.error("Välj en depåplats."); return; }
    setSubmitting(true);
    try {
      const res = await api.createDepotBooking(form);
      setDone(res);
      toast.success("Depåplats bokad!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunde inte boka.");
      setForm((f) => ({ ...f, spot_number: "" }));
      loadSpots();
    } finally {
      setSubmitting(false);
    }
  };

  const field = "mt-1.5 bg-[#14161A] border-white/15 text-white placeholder:text-white/30";
  const AREA_NAMES = { A: "Depå A · Pendlarparkering", B: "Depå B · Kolgrillen", C: "Depå C · Parkvägen", D: "Depå D · CIK-parkering", E: "Depå E · Södervägen", F: "Depå F · Badhuset" };
  const areas = [...new Set(spots.map((s) => s.area).filter(Boolean))].sort();
  const areaSpots = spots.filter((s) => s.area === area);

  return (
    <>
      <PageHeader kicker="För förare" title="Boka depåplats" subtitle="Reservera din depåplats till Novemberkåsan 2026. Anmälan via Svemo TA krävs." image={DEPOT_IMG} />
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-6">
        <a href="#hitta-min-depaplats" data-testid="depot-lookup-anchor" className="inline-flex items-center gap-2 rounded-full border border-amber-500/50 px-4 py-2 text-sm font-semibold text-amber-500 hover:bg-amber-500/10 transition-colors">Redan bokat? Hitta min depåplats →</a>
      </div>
      <DepotLookup />

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid gap-10 lg:grid-cols-2">
          {/* Map */}
          <Reveal>
            <div>
              <SectionHeader kicker="Karta" title="Depå & parkering" />
              <a href="/depa/oversikt-syd.png" target="_blank" rel="noreferrer" className="mt-6 block overflow-hidden rounded-2xl border border-white/10 group">
                <img src="/depa/oversikt-syd.png" alt="Översiktskarta Syd – depåområden Knivsta" loading="lazy" className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </a>
              <p className="mt-3 text-xs font-mono text-white/55">Alla depåområden och platser visas längre ner. Klicka för full storlek.</p>
            </div>
          </Reveal>

          {/* Form / confirmation */}
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-white/10 bg-[#0F1114] p-6 sm:p-8">
              {done ? (
                <div className="text-center py-8" data-testid="depot-confirmation">
                  <CheckCircle2 className="w-16 h-16 text-amber-500 mx-auto" />
                  <h3 className="mt-4 font-display text-3xl font-black uppercase text-white">Depåplats bokad!</h3>
                  <p className="mt-2 text-white/60">Din plats <span className="text-amber-500 font-bold">{done.spot_number}</span> är reserverad.</p>
                  <div className="mt-6 mx-auto max-w-xs text-left rounded-xl border border-white/10 bg-[#14161A] p-5 space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-white/50">Depåplats</span><span className="text-white font-semibold">{done.spot_number}</span></div>
                    <div className="flex justify-between"><span className="text-white/50">Namn</span><span className="text-white">{done.name}</span></div>
                    <div className="flex justify-between"><span className="text-white/50">Svemo TA</span><span className="text-white">{done.svemo_ta}</span></div>
                    <div className="flex justify-between"><span className="text-white/50">Telefon</span><span className="text-white">{done.phone}</span></div>
                  </div>
                  <p className="mt-4 text-sm text-white/50">
                    {done.email_sent ? `En bekräftelse har skickats till ${done.email}.` : `Bokningen är registrerad (bekräftelsemail kunde inte skickas just nu).`}
                  </p>
                  <button
                    type="button"
                    data-testid="depot-book-another"
                    onClick={() => { setDone(null); setForm({ spot_number: "", name: "", email: "", phone: "", svemo_ta: "" }); loadSpots(); }}
                    className="mt-6 inline-flex rounded-full border border-white/20 px-6 py-3 text-sm font-bold uppercase tracking-widest text-white hover:border-amber-500 hover:text-amber-500 transition-colors"
                  >
                    Boka en till plats
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="depot-form" className="space-y-4">
                  <SectionHeader kicker="Bokning" title="Din depåplats" />
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 flex gap-2 text-sm text-white/70">
                    <Info className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span>Du måste ha anmält dig via Svemo TA och ange ditt TA-nummer för att boka.</span>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70 text-xs uppercase tracking-wide">Depåområde</Label>
                      <Select value={area} onValueChange={(v) => { setArea(v); setForm({ ...form, spot_number: "" }); }}>
                        <SelectTrigger className={field} data-testid="depot-area-select">
                          <SelectValue placeholder={loadingSpots ? "Laddar..." : "Välj område"} />
                        </SelectTrigger>
                        <SelectContent>
                          {areas.map((a) => (
                            <SelectItem key={a} value={a}>{AREA_NAMES[a] || `Depå ${a}`} ({spots.filter((s) => s.area === a).length} lediga)</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white/70 text-xs uppercase tracking-wide">Plats</Label>
                      <Select value={form.spot_number} onValueChange={(v) => setForm({ ...form, spot_number: v })} disabled={!area}>
                        <SelectTrigger className={field} data-testid="depot-spot-select">
                          <SelectValue placeholder={!area ? "Välj område först" : areaSpots.length ? "Välj plats" : "Inga lediga"} />
                        </SelectTrigger>
                        <SelectContent>
                          {areaSpots.map((s) => <SelectItem key={s.spot_number} value={s.spot_number}>{`${s.label} (${s.spot_number})`}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-xs font-mono text-white/55">{spots.length} lediga platser totalt · Team-depån bokas på plats (100 kr/löpmeter).</p>

                  <div><Label className="text-white/70 text-xs uppercase tracking-wide">Namn</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="depot-name" /></div>
                  <div><Label className="text-white/70 text-xs uppercase tracking-wide">E-post</Label>
                    <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} data-testid="depot-email" /></div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-white/70 text-xs uppercase tracking-wide">Mobilnummer</Label>
                      <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} data-testid="depot-phone" /></div>
                    <div><Label className="text-white/70 text-xs uppercase tracking-wide">Svemo TA-nummer</Label>
                      <Input required value={form.svemo_ta} onChange={(e) => setForm({ ...form, svemo_ta: e.target.value })} className={field} data-testid="depot-svemo" placeholder="t.ex. 123456" /></div>
                  </div>

                  <Button type="submit" disabled={submitting || !spots.length} data-testid="depot-submit" className="w-full h-12 bg-amber-500 text-black hover:bg-amber-400 font-bold uppercase tracking-widest">
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Tent className="w-4 h-4 mr-2" /> Boka depåplats</>}
                  </Button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <DepotAreas />
    </>
  );
}
